import React, { useState } from "react";

import { useMutation } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";

import Header from "@/components/MealPlanForm/Header";
import MealPlanFormContainer from "@/components/MealPlanForm/MealPlanFormContainer";
import Step from "@/components/MealPlanForm/Step";
import MealPlanFormStore from "@/components/MealPlanForm/store/MealPlanFormStore";
import { graphql } from "@/gql";
import { MealPlanTemplateAttributes } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import TemplateDetailsForm, {
  TemplateDetailsFormValues,
} from "../components/TemplateDetailsForm";
import { mealTemplatesPageQuery } from "../Index/MealTemplatesPage";

const createMealPlanTemplateMutation = graphql(`
  mutation CreateMealPlanTemplate($input: CreateMealPlanTemplateInput!) {
    createMealPlanTemplate(input: $input) {
      mealPlanTemplate {
        id
        name
        description
        durationDays
        recommendedCalories
      }
    }
  }
`);

const NewMealPlanTemplatePage: React.FC = () => {
  const snackbar = useGlobalSnackbar();
  const navigate = useNavigate();
  const [createMealPlanTemplate] = useMutation(createMealPlanTemplateMutation);

  const [store] = useState(new MealPlanFormStore("template"));

  const onSubmit = async (values: TemplateDetailsFormValues) => {
    try {
      const result = await createMealPlanTemplate({
        variables: {
          input: {
            attributes: {
              name: values.name,
              description: values.description,
              durationDays: values.durationDays,
              recommendedCalories: values.recommendedCalories,
            } as MealPlanTemplateAttributes,
          },
        },
        refetchQueries: [
          { query: mealTemplatesPageQuery, variables: { name: "" } },
        ],
      });
      const id = result.data?.createMealPlanTemplate.mealPlanTemplate.id;
      snackbar.open("Meal plan template created successfully");
      navigate(`/templates/meals/${id}/edit`, { replace: true });
    } catch (e) {
      snackbar.open(displayError(e, "Unable to create meal plan template"));
    }
  };

  return (
    <Box paddingY={4}>
      <Box marginBottom={4}>
        <Button
          component={Link}
          to="/templates/meals"
          variant="text"
          startIcon={<ArrowBackIcon fontSize="small" color="primary" />}
        >
          <Typography variant="body1" color={colors.black}>
            Back to templates
          </Typography>
        </Button>
      </Box>
      <MealPlanFormContainer
        store={store}
        header={
          <Header
            title="Meal Template"
            steps={["Details", "Planning", "Meal plan"]}
          />
        }
      >
        <Step index={0}>
          <TemplateDetailsForm submitLabel="Save" onSubmit={onSubmit} />
        </Step>
      </MealPlanFormContainer>
    </Box>
  );
};

export default NewMealPlanTemplatePage;
